<template>
  <div class="access-category">
    <div class="font-medium mb-4">Regions</div>
    <div class="flex gap-4 items-start">
      <RadioTabbed
        v-if="authStore.details.userType == 1"
        v-model="state.regionsAll"
        :options="[
          { value: true, label: 'All' },
          { value: false, label: 'Selection' }
        ]"
        @option-change="onSelectionChange"
      />
      <Select
        v-model="selectedRegions"
        mode="multiple"
        chips
        :chips-displayed="10"
        auto-expand
        :close-on-select="false"
        handle-search
        :hide-selected="true"
        value-key="id"
        label-key="name"
        :placeholder="'None selected'"
        :options="accessStore.accessAllowed.regions"
        :readonly="state.regionsAll"
        @change="onSelectionChange"
      />
    </div>
  </div>

  <div class="access-category">
    <div class="font-medium mb-4">Manufacturers</div>
    <div class="flex gap-4 items-start">
      <RadioTabbed
        v-if="authStore.details.userType == 1"
        v-model="state.manufacturersAll"
        :options="[
          { value: true, label: 'All' },
          { value: false, label: 'Selection' }
        ]"
        @option-change="onSelectionChange"
      />
      <Select
        v-if="dropdownAccessOptions.manufacturers.length"
        v-model="selectedManufacturers"
        mode="multiple"
        chips
        :chips-displayed="10"
        auto-expand
        :close-on-select="false"
        :searchable="true"
        value-key="id"
        label-key="name"
        :placeholder="'None selected'"
        :options="dropdownAccessOptions.manufacturers"
        :readonly="state.manufacturersAll"
        @change="onSelectionChange"
      />

      <div
        v-if="!dropdownAccessOptions.manufacturers.length"
        class="text-warning flex gap-2 items-center mt-2"
      >
        <Icon type="triangle-exclamation" />
        <div>No manufacturers found in the selected regions</div>
      </div>
    </div>
  </div>

  <div class="access-category">
    <div class="font-medium mb-4">Groups</div>
    <div class="flex gap-4 items-start">
      <RadioTabbed
        v-if="authStore.details.userType == 1"
        v-model="state.groupsAll"
        :options="[
          { value: true, label: 'All' },
          { value: false, label: 'Selection' }
        ]"
        @option-change="onSelectionChange"
      />
      <Select
        v-if="dropdownAccessOptions.groups.length"
        v-model="selectedGroups"
        mode="multiple"
        chips
        :chips-displayed="10"
        auto-expand
        :close-on-select="false"
        :searchable="true"
        value-key="id"
        label-key="name"
        :placeholder="'None selected'"
        :options="dropdownAccessOptions.groups"
        :readonly="state.groupsAll"
        @change="onSelectionChange"
      />
      <div
        v-if="!dropdownAccessOptions.groups.length"
        class="text-warning flex gap-2 items-center mt-2"
      >
        <Icon type="triangle-exclamation" />
        <div>No groups found in the selected manufacturers</div>
      </div>
    </div>
  </div>

  <div class="access-category">
    <div class="font-medium mb-4">Dealerships</div>
    <div class="flex gap-4 items-start">
      <RadioTabbed
        v-if="authStore.details.userType == 1"
        v-model="state.dealershipsAll"
        :options="[
          { value: true, label: 'All' },
          { value: false, label: 'Selection' }
        ]"
        @option-change="onSelectionChange"
      />
      <Select
        v-if="dropdownAccessOptions.dealerships.length"
        v-model="selectedDealerships"
        mode="multiple"
        chips
        :chips-displayed="10"
        auto-expand
        searchable
        :close-on-select="false"
        value-key="id"
        label-key="name"
        :placeholder="'None selected'"
        :options="dropdownAccessOptions.dealerships"
        :readonly="state.dealershipsAll"
        @change="onSelectionChange"
      />
      <div
        v-if="!dropdownAccessOptions.dealerships.length"
        class="text-warning flex gap-2 items-center mt-2"
      >
        <Icon type="triangle-exclamation" />
        <div>No dealerships found in the selected groups</div>
      </div>
    </div>
  </div>

  <Alert
    v-if="selectedDealerships.length > 10"
    severity="warning"
    icon="triangle-exclamation"
    :title="`Large Selection`"
    :message="`You are giving access to ${selectedDealerships.length} dealerships. Please make sure this is correct.`"
    :show-dismiss-button="false"
    :show-close-button="false"
    class="mt-4"
  />
</template>

<script setup>
import { watch, ref, nextTick, computed } from 'vue'
import Select from '@/components/forms/Select.vue'
import RadioTabbed from '@/components/forms/RadioTabbed.vue'
import Icon from '@/components/icon/Icon.vue'
import Alert from '@/components/alert/Alert.vue'

import { useAuthStore } from '@/stores/AuthStore'
import { useAccessStore } from '@/stores/AccessStore'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  }
})
const emit = defineEmits(['update:modelValue'])
const authStore = useAuthStore()
const accessStore = useAccessStore()

const emptyAccessWarning = ref(null)

const defaultState = {
  regions: [],
  regionsAll: false,
  manufacturers: [],
  manufacturersAll: false,
  groups: [],
  groupsAll: false,
  dealerships: [],
  dealershipsAll: false
}
const state = ref(defaultState)

const dropdownAccessOptions = ref({})

// Add computed properties for selected values
const selectedRegions = computed({
  get: () => (state.value.regionsAll ? accessStore.accessAllowed.regions.map(r => r.id) : state.value.regions),
  set: val => {
    if (!state.value.regionsAll) state.value.regions = val
  }
})

const selectedManufacturers = computed({
  get: () =>
    state.value.manufacturersAll ? dropdownAccessOptions.value.manufacturers.map(m => m.id) : state.value.manufacturers,
  set: val => {
    if (!state.value.manufacturersAll) state.value.manufacturers = val
  }
})

const selectedGroups = computed({
  get: () => (state.value.groupsAll ? dropdownAccessOptions.value.groups.map(g => g.id) : state.value.groups),
  set: val => {
    if (!state.value.groupsAll) state.value.groups = val
  }
})

const selectedDealerships = computed({
  get: () =>
    state.value.dealershipsAll ? dropdownAccessOptions.value.dealerships.map(d => d.id) : state.value.dealerships,
  set: val => {
    if (!state.value.dealershipsAll) state.value.dealerships = val
  }
})

function updateOptions() {
  const newOptions = {}

  newOptions.manufacturers = accessStore.accessAllowed.manufacturers.filter(manufacturer => {
    return state.value.regionsAll || manufacturer.regions.some(region => state.value.regions.includes(region))
  })

  newOptions.groups = accessStore.accessAllowed.groups.filter(group => {
    return (
      (state.value.regionsAll || group.regions.some(region => state.value.regions.includes(region))) &&
      (state.value.manufacturersAll ||
        group.manufacturers.some(manufacturer => state.value.manufacturers.includes(manufacturer)))
    )
  })

  newOptions.dealerships = accessStore.accessAllowed.dealerships.filter(dealership => {
    return (
      (state.value.manufacturersAll ||
        dealership.manufacturers.some(manufacturer => state.value.manufacturers.includes(manufacturer))) &&
      (state.value.groupsAll || state.value.groups.some(group => group === dealership.groupId))
    )
  })

  // Remove selected items from dropdowns if no longer in newOptions

  // Check if selected dealerships are still available in the new options
  state.value.dealerships = state.value.dealerships.filter(dealership => {
    return newOptions.dealerships.some(newDealership => newDealership.id === dealership)
  })
  // Check if selected groups are still available in the new options
  state.value.groups = state.value.groups.filter(group => {
    return newOptions.groups.some(newGroup => newGroup.id === group)
  })
  // Check if selected manufacturers are still available in the new options
  state.value.manufacturers = state.value.manufacturers.filter(manufacturer => {
    return newOptions.manufacturers.some(newManufacturer => newManufacturer.id === manufacturer)
  })

  dropdownAccessOptions.value = newOptions
}
updateOptions()
// Watch for changes in the modelValue and update the internal state
watch(
  () => props.modelValue,

  newValue => {
    if (newValue) {
      state.value = newValue
    }
    updateOptions()
  },
  { immediate: true }
)

function onSelectionChange() {
  nextTick(() => {
    // When switching from "All" to "Selection", clear all downstream selections
    if (!state.value.regionsAll && state.value.regions.length === 0) {
      state.value.manufacturers = []
      state.value.groups = []
      state.value.dealerships = []
      // Also reset the "All" flags for downstream selections
      state.value.manufacturersAll = false
      state.value.groupsAll = false
      state.value.dealershipsAll = false
    }
    if (!state.value.manufacturersAll && state.value.manufacturers.length === 0) {
      state.value.groups = []
      state.value.dealerships = []
      // Reset the "All" flags for downstream selections
      state.value.groupsAll = false
      state.value.dealershipsAll = false
    }
    if (!state.value.groupsAll && state.value.groups.length === 0) {
      state.value.dealerships = []
      // Reset the "All" flag for dealerships
      state.value.dealershipsAll = false
    }

    // Clear arrays when switching to "All"
    if (state.value.regionsAll) {
      state.value.regions = []
    }
    if (state.value.manufacturersAll) {
      state.value.manufacturers = []
    }
    if (state.value.groupsAll) {
      state.value.groups = []
    }
    if (state.value.dealershipsAll) {
      state.value.dealerships = []
    }

    updateOptions()
  })
}

// Watch for changes in the internal state and emit an update
watch(
  state,
  newValue => {
    emit('update:modelValue', newValue)

    // Check if each has atleast all or 1 item selected. If not, show warning message in UI
    if (
      (state.value.regionsAll == false && state.value.regions.length == 0) ||
      (state.value.manufacturersAll == false && state.value.manufacturers.length == 0) ||
      (state.value.groupsAll == false && state.value.groups.length == 0) ||
      (state.value.dealershipsAll == false && state.value.dealerships.length == 0)
    ) {
      emptyAccessWarning.value = true
    } else {
      emptyAccessWarning.value = false
    }
  },
  { deep: true }
)
</script>

<style>
.access-category {
  border: 1px solid var(--border-secondary);
  border-radius: var(--rounded-lg);
  margin-bottom: var(--s-4);
  padding: var(--s-4);
}
</style>
